import type { PriceData, PriceDataItem } from '@/lib/types/priceData'
import type { TradeData, TradeDataSet } from '@/lib/types/tradeData'

export function generateMockPriceData(
  symbol: string,
  basePrice: number,
  hours = 24,
): PriceData {
  const data: PriceDataItem[] = []
  const now = Date.now()
  const oneHour = 60 * 60 * 1000

  let currentPrice = basePrice

  for (let i = hours - 1; i >= 0; i--) {
    const timestamp = now - (i * oneHour)

    // Generate realistic price movement (±2% per hour max)
    const volatility = 0.02
    const change = (Math.random() - 0.5) * 2 * volatility
    const priceChange = currentPrice * change

    // Add some trend bias
    const trendBias = Math.sin((hours - i) / hours * Math.PI) * 0.005
    currentPrice = currentPrice + priceChange + (currentPrice * trendBias)

    // Generate OHLC data
    const high = currentPrice * (1 + Math.random() * 0.01)
    const low = currentPrice * (1 - Math.random() * 0.01)
    const open = i === hours - 1 ? basePrice : data.at(-1)?.close || currentPrice
    const close = currentPrice
    const volume = Math.random() * 1000000 + 500000

    data.push({
      timestamp,
      price: currentPrice,
      volume,
      open,
      high,
      low,
      close,
    })
  }

  return {
    symbol,
    data,
    timeframe: '1h',
    lastUpdate: now,
  }
}

export function generateMockTradeData(
  symbol: string,
  priceData: PriceData,
  isLongPosition: boolean,
  leverage: number,
): TradeDataSet {
  const trades: TradeData[] = []

  // Generate 3-8 trades across the price data timeframe
  const numTrades = Math.floor(Math.random() * 6) + 3
  const selectedIndices = new Set<number>()

  // Randomly select price data points for trades
  while (selectedIndices.size < numTrades && selectedIndices.size < priceData.data.length) {
    const randomIndex = Math.floor(Math.random() * priceData.data.length)
    selectedIndices.add(randomIndex)
  }

  Array.from(selectedIndices).sort((a, b) => a - b).forEach((index) => {
    const pricePoint = priceData.data[index]
    if (!pricePoint)
      return

    const tradeType = Math.random() > 0.6 ? (isLongPosition ? 'buy' : 'sell') : (isLongPosition ? 'sell' : 'buy')
    const amount = Math.random() * 2 + 0.1 // 0.1 to 2.1 BTC
    const price = pricePoint.price * (1 + (Math.random() - 0.5) * 0.002) // ±0.1% price variation
    const total = amount * price
    const size = total * leverage
    const positionValue = total

    trades.push({
      timestamp: pricePoint.timestamp,
      type: tradeType,
      price,
      amount: Number.parseFloat(amount.toFixed(4)),
      total: Number.parseFloat(total.toFixed(2)),
      leverage,
      size: Number.parseFloat(size.toFixed(2)),
      positionValue: Number.parseFloat(positionValue.toFixed(2)),
    })
  })

  return {
    symbol,
    trades: trades.sort((a, b) => a.timestamp - b.timestamp),
  }
}
